import type { FormInstance } from 'antd';
import { useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { apiBaseUrl as baseUrl } from '../config';
import i18n from '../i18n';
import { authorizedFetch } from './authorizedFetch';

/**
 * A violation from the API: schema errors carry a `message`; business-rule violations (BR-xx)
 * carry a `rule`, a translatable `code` with `params`, a `path` in the request and a severity.
 */
export interface Violation {
  field: string;
  message?: string;
  rule?: string;
  code?: string;
  path?: string;
  index?: number;
  severity?: 'BLOCK' | 'WARN';
  exemptable?: boolean;
  params?: Record<string, string | number>;
}

export interface ProblemDetails {
  title: string;
  status: number;
  code: string;
  violations?: Violation[];
}

/** Text of a violation in the user's language, prefixed by its business rule. */
export function violationText(v: Violation): string {
  if (v.code && v.rule) {
    const text = i18n.t(`rules.${v.code}`, { ...v.params, defaultValue: v.code });
    return `${v.rule} · ${text}`;
  }
  return v.message ?? v.field;
}

export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails) {
    super(problem.title);
  }
}

export type ApiFetch = <T>(path: string, init?: RequestInit) => Promise<T>;

/** Sent when the API asks for two-step verification: the portal shows how to set it up. */
export const TWO_STEP_REQUIRED_EVENT = 'wm:two-step-required';

/** Sent when the API answers that the vendor suspended the organisation: the portal says so. */
export const TENANT_SUSPENDED_EVENT = 'wm:tenant-suspended';

/** The ApiError of a refused response, its problem+json body when there is one. */
async function apiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as Partial<ProblemDetails> | null;
  if (body?.code === 'two_step_required') window.dispatchEvent(new Event(TWO_STEP_REQUIRED_EVENT));
  if (body?.code === 'tenant_suspended') window.dispatchEvent(new Event(TENANT_SUSPENDED_EVENT));
  return new ApiError({
    title: response.statusText,
    code: 'error',
    ...body,
    status: response.status,
  });
}

/**
 * Fetch wrapper adding the bearer token (renewed after a 401, see authorizedFetch) and turning
 * problem+json responses into ApiError.
 */
export function useApi(): ApiFetch {
  const { accessToken, renewSession } = useAuth();
  return useCallback(
    async <T>(path: string, init: RequestInit = {}): Promise<T> => {
      const headers = new Headers(init.headers);
      headers.set('Accept', 'application/json');
      if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type'))
        headers.set('Content-Type', 'application/json');
      const response = await authorizedFetch(
        `${baseUrl}${path}`,
        { ...init, headers },
        accessToken(),
        renewSession,
      );
      if (!response.ok) throw await apiError(response);
      if (response.status === 204) return undefined as T;
      const text = await response.text();
      return (text ? JSON.parse(text) : null) as T;
    },
    [accessToken, renewSession],
  );
}

/** Puts the API's field violations (422) on the matching form fields; returns true when it did. */
export function applyViolations(form: FormInstance, error: unknown): boolean {
  if (!(error instanceof ApiError) || !error.problem.violations?.length) return false;
  form.setFields(
    error.problem.violations.map((v) => ({
      name: (v.path ?? v.field).split('.').map((p) => (/^\d+$/.test(p) ? Number(p) : p)),
      errors: [violationText(v)],
    })),
  );
  return true;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const v = error.problem.violations;
    return v?.length ? v.map(violationText).join(' · ') : error.problem.title;
  }
  return error instanceof Error ? error.message : String(error);
}

/** Fetches a protected file (PDF, image) with the bearer token and returns a blob URL. */
export function useFileUrl() {
  const { accessToken, renewSession } = useAuth();
  return useCallback(
    async (path: string, init: RequestInit = {}): Promise<string> => {
      const headers = new Headers(init.headers);
      if (init.body && !(init.body instanceof FormData))
        headers.set('Content-Type', 'application/json');
      const response = await authorizedFetch(
        `${baseUrl}${path}`,
        { ...init, headers },
        accessToken(),
        renewSession,
      );
      if (!response.ok) throw await apiError(response);
      return URL.createObjectURL(await response.blob());
    },
    [accessToken, renewSession],
  );
}

/** Downloads a protected file (exports) under the name the API gives it. */
export function useDownload() {
  const { accessToken, renewSession } = useAuth();
  return useCallback(
    async (path: string, fallbackName: string) => {
      const response = await authorizedFetch(`${baseUrl}${path}`, {}, accessToken(), renewSession);
      if (!response.ok) throw await apiError(response);
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const match = /filename="([^"]+)"/.exec(disposition);
      const name = match?.[1] ? decodeURIComponent(match[1]) : fallbackName;
      const url = URL.createObjectURL(await response.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    [accessToken, renewSession],
  );
}
